import { useCallback, useEffect, useRef, useState } from 'react'

export type HoverProps = {
  onMouseEnter: () => void
  onMouseLeave: () => void
}

/**
 * Tracks "hovering one of N elements" with a grace period: leaving one element
 * schedules a hide; entering another (or re-entering the same one) within the
 * delay cancels it. Lets the cursor travel between adjacent elements without
 * the group flickering closed.
 *
 * Spread the returned `hoverProps` on every element that should keep the group
 * "hovered" (e.g. a trigger and the popover it reveals).
 */
export function useGroupHover(delayMs: number): { isHovered: boolean; hoverProps: HoverProps } {
  const [isHovered, setIsHovered] = useState(false)
  const timeoutRef = useRef<number | null>(null)

  const handleEnter = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setIsHovered(true)
  }, [])

  const handleLeave = useCallback(() => {
    if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current)
    timeoutRef.current = window.setTimeout(() => {
      setIsHovered(false)
      timeoutRef.current = null
    }, delayMs)
  }, [delayMs])

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) window.clearTimeout(timeoutRef.current)
    },
    [],
  )

  return { isHovered, hoverProps: { onMouseEnter: handleEnter, onMouseLeave: handleLeave } }
}
